import './file-uploader.scss';

import {
  Box,
  BoxProps,
  Grid,
  IconButton,
  LinearProgress,
  Stack,
  styled,
} from '@mui/material';
import { DropzoneOptions, FileRejection, useDropzone } from 'react-dropzone';
import React, { useCallback, useState } from 'react';
import config, { convergeLogger } from '@converge/config';

import { ApolloError } from '@apollo/client';
import CloseIcon from '@mui/icons-material/Close';
import { ConvergeInputLabel } from './converge-input-label';
import { ConvergeTooltip } from './converge-tooltip';
import DescriptionIcon from '@mui/icons-material/Description';
import { NewConvergeButton } from './new-button';
import { UploadFolderFormSearchableSelectField } from './form-searchable-select-field';
import { UploadFolderName } from '@converge/graphql';
import WarningIcon from '@mui/icons-material/Warning';
import { bytesToSize } from '@converge/utils';
import { useConvergeSnackBar } from '../../contexts';
import { v4 as uuidv4 } from 'uuid';
import { ALLOWED_EXTENSIONS } from './file-upload-dropzone';

const hasInvalidFileNameChars = (fileName: string): boolean => {
  const invalidChars = /[*?<>|\\/:]/;
  return invalidChars.test(fileName);
};

const hasValidExtension = (fileName: string): boolean => {
  const parts = fileName?.split('.');
  if (parts?.length === 1) {
    return false;
  }
  const extension = parts?.pop()?.toLowerCase();
  return extension ? ALLOWED_EXTENSIONS.includes(extension) : false;
};

const getColor = (props: IUploadWrapperContainer) => {
  if (props.isDragAccept) {
    return '#59b959';
  }
  if (props.isDragReject) {
    return '#db524c';
  }
  if (props.isFocused) {
    return '#0d1a58';
  }
  return '#C4C4C4';
};

const UploadWrapperContainer = styled(Box, {
  shouldForwardProp: (prop) =>
    !['isDragAccept', 'isDragReject', 'isFocused'].includes(prop as string),
})<IUploadWrapperContainer>(({ isDragAccept, isDragReject, isFocused }) => ({
  flex: '1',
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  padding: '40px',
  borderWidth: '2px',
  borderRadius: '10px',
  borderColor: `${getColor({ isDragAccept, isDragReject, isFocused })}`,
  borderStyle: 'dashed',
  backgroundColor: '#C4C4C40d',
  color: '#0d1957',
  fontWeight: 'bold',
  fontSize: '1.4rem',
  outline: 'none',
  transition: ' border 0.24s ease-in-out',
}));
const UploadWrapper = styled(Box)((props) => ({
  [props.theme.breakpoints.up('md')]: {
    display: 'flex',
  },
}));
const UploadProgressWrapper = styled(Box)((props) => ({
  height: '294px',
  overflowY: 'auto',
  paddingRight: '15px',
  [props.theme.breakpoints.up('md')]: {
    flexBasis: '55%',
  },
}));

interface IUploadWrapperContainer extends BoxProps {
  isDragAccept: boolean;
  isDragReject: boolean;
  isFocused: boolean;
}
export interface IUploadFile {
  id: string;
  file: File;
  destination?: string;
  error?: Error | ApolloError;
  folder: UploadFolderName;
  url?: string;
  progress?: number;
}

interface IUploadFileUploader extends DropzoneOptions {
  onFileHandler: (data: IUploadOnDropProps) => void;
  onFileRemove?: (acceptedFiles: IUploadFile[]) => void;
  label: string;
  hiddenLabel?: boolean;
  required?: boolean;
  progressBar?: boolean;
  showRemoveIcon?: boolean;
  enableFolderSelection?: boolean;
  folder?: UploadFolderName;
  verb?: 'upload' | 'attach';
  existingFiles?: { name: string; folder: string }[];
}

export interface IUploadProgress {
  [key: string]: number;
}
export interface IUploadOnDropProps {
  acceptedFiles: IUploadFile[];
  fileRejections: FileRejection[];
  allAcceptedFiles: IUploadFile[];
  onProgressChange: (event: ProgressEvent, file: IUploadFile) => void;
  onError: (error: Error | ApolloError, file: IUploadFile) => void;
}

export const FileUploader: React.FC<IUploadFileUploader> = ({
  onFileHandler,
  onFileRemove,
  label,
  hiddenLabel = false,
  required = false,
  progressBar = true,
  showRemoveIcon = false,
  verb = 'upload',
  maxSize = config.maxFileSizeInBytes,
  maxFiles = config.maxFilesPerUpload,
  enableFolderSelection = false,
  folder = UploadFolderName.ApplicationForms,
  existingFiles = [],
  ...otherProps
}) => {
  const [targetFolder, setTargetFolder] = useState<UploadFolderName | null>(
    folder ?? null
  );
  const [uploadProgress, setUploadProgress] = useState<IUploadProgress>({});
  const [files, setFiles] = useState<IUploadFile[]>([]);
  const { notify } = useConvergeSnackBar();

  const onProgressChange = useCallback(
    (event: ProgressEvent, file: IUploadFile) => {
      setUploadProgress((prevProgress) => ({
        ...prevProgress,
        [file.id]: Math.round((event.loaded * 100) / event.total),
      }));
    },
    []
  );

  const normalizeFolder = (f: string) => f.toUpperCase().replace(/\s+/g, '_');

  const onDropHandle = useCallback(
    (acceptedFiles: File[], fileRejections: FileRejection[]) => {
      const newFolder = targetFolder ?? folder;
      const existingNames = new Set([
        ...files.map((f) => `${f.file.name}|${normalizeFolder(f.folder)}`),
        ...existingFiles.map((f) => `${f.name}|${normalizeFolder(f.folder)}`),
      ]);
      const invalidCharFiles: string[] = [];
      const invalidExtFiles: string[] = [];
      const duplicateFiles: string[] = [];
      const validFiles: File[] = [];

      acceptedFiles.forEach((file) => {
        if (hasInvalidFileNameChars(file.name)) {
          invalidCharFiles.push(file.name);
        } else if (!hasValidExtension(file?.name)) {
          invalidExtFiles.push(file.name);
        } else if (
          existingNames.has(`${file.name}|${normalizeFolder(newFolder)}`)
        ) {
          duplicateFiles.push(file.name);
        } else {
          validFiles.push(file);
        }
      });

      if (invalidCharFiles.length > 0 && invalidExtFiles.length > 0) {
        notify(
          `Invalid file name and extension. Please remove special characters such as *, ?, <, >, |, \\, /, :, " and ensure file type is one of: ${ALLOWED_EXTENSIONS.join(
            ', '
          )}`,
          {
            variant: 'error',
          }
        );
      } else if (invalidCharFiles.length > 0) {
        notify(
          `Invalid characters in file name. Please remove any special characters such as *, ?, <, >, |, \\, /, :, " before uploading.`,
          {
            variant: 'error',
          }
        );
      } else if (invalidExtFiles.length > 0) {
        notify(
          `Invalid file extension. Only the following file types are allowed: ${ALLOWED_EXTENSIONS.join(
            ', '
          )}`,
          {
            variant: 'error',
          }
        );
      }

      if (duplicateFiles.length > 0) {
        notify(
          `Duplicate file${
            duplicateFiles.length > 1 ? 's' : ''
          } skipped: ${duplicateFiles.join(
            ', '
          )}. A file with the same name has already been added.`,
          {
            variant: 'error',
          }
        );
      }

      const newAcceptedFiles: IUploadFile[] = validFiles.map(
        (file): IUploadFile => {
          return {
            id: uuidv4(),
            file,
            folder: targetFolder ?? folder ?? undefined,
          };
        }
      );
      const allFiles = [...files, ...newAcceptedFiles];
      convergeLogger.log(`All files:`, allFiles);
      setFiles(() => [...allFiles]);
      onFileHandler({
        acceptedFiles: newAcceptedFiles,
        fileRejections,
        allAcceptedFiles: allFiles,
        onProgressChange,
        onError: (error: Error | ApolloError, file: IUploadFile) => {
          setFiles((prevFiles) => {
            const fileToUpdate = prevFiles.find((f) => f.id === file.id);
            if (fileToUpdate) {
              fileToUpdate.error = error;
            }
            return prevFiles;
          });
        },
      });
    },
    [
      files,
      folder,
      existingFiles,
      onFileHandler,
      onProgressChange,
      targetFolder,
      notify,
    ]
  );

  const onRemove = useCallback(
    (id: string) => {
      const previousUploadProgress = uploadProgress;
      delete previousUploadProgress[id];
      setUploadProgress({ ...previousUploadProgress });
      const allFiles = files.filter((file) => file.id !== id);
      setFiles(allFiles);
      if (onFileRemove) {
        onFileRemove(allFiles);
      }
    },
    [files, onFileRemove, uploadProgress]
  );

  const {
    getRootProps,
    getInputProps,
    open,
    isDragAccept,
    isFocused,
    isDragReject,
  } = useDropzone({
    onDrop: onDropHandle,
    noClick: true,
    noKeyboard: true,
    maxSize,
    maxFiles,
    validator: (file) => {
      if (!hasValidExtension(file?.name)) {
        return {
          code: 'invalid-extension',
          message: `Invalid file extension. Only the following file types are allowed: ${ALLOWED_EXTENSIONS.join(
            ', '
          )}`,
        };
      }
      return null;
    },
    onDropRejected: (rejectedFiles) => {
      const isTooManyFiles = rejectedFiles.some((f) =>
        f.errors.some((e) => e.code === 'too-many-files')
      );
      if (isTooManyFiles) {
        notify({
          message: `You can upload up to ${maxFiles} files at a time. Once uploaded, you can select another batch.`,
          variant: 'error',
        });
        return;
      }
      const errorMessage = (
        rejectedFiles[0]?.errors[0]?.message ?? 'Unknown error'
      ).replace(`${maxSize} bytes`, `${maxSize / 1000000} MB`);

      notify({
        message: `Unable to ${verb} file: ${errorMessage}`,
        variant: 'error',
      });
    },
    ...otherProps,
  });

  return (
    <Grid item xs={12}>
      {hiddenLabel !== true && (
        <ConvergeInputLabel label={label} required={required} />
      )}
      <UploadWrapper className="file-upload">
        <UploadWrapperContainer
          className="file-upload-dropbox"
          {...getRootProps({ isDragAccept, isFocused, isDragReject })}
        >
          <input {...getInputProps()} data-testid="file-input" />

          <Stack
            justifyContent={'center'}
            flexDirection={'row'}
            alignItems={'center'}
            gap={2}
          >
            <Box>
              <p className="file-uploader-drop-file-area">
                Drop a file here to {verb}
              </p>
            </Box>
            <Box>
              <p className="file-uploader-p">
                <small>or</small>
              </p>
            </Box>
            <Box>
              <NewConvergeButton onClick={open} label="Choose File" />
            </Box>
          </Stack>

          <Box sx={{ mt: 1 }}>
            <p className="file-uploader-p">
              <small>
                Max file size: {Math.round(maxSize / 1000000)} MB &middot;
                Accepted: {ALLOWED_EXTENSIONS.join(', ')}
              </small>
            </p>
          </Box>

          {enableFolderSelection && (
            <>
              <p className="specify-folder-label">
                <small>Specify a folder:</small>
              </p>
              <Stack className="specify-folder-dropdown">
                <UploadFolderFormSearchableSelectField
                  minDropdownWidth="330px"
                  maxDropdownWidth="380px"
                  name={'folder'}
                  placeholder="Select Folder"
                  hideLabel
                  value={targetFolder ?? undefined}
                  onChangeOption={(option) => {
                    setTargetFolder(option?.data?.name ?? null);
                  }}
                  containerWidth={12}
                />
              </Stack>
            </>
          )}
        </UploadWrapperContainer>
        {files && files.length ? (
          <UploadProgressWrapper className="file-upload-progress-bar-wrapper">
            {files.map((file, index) => (
              <Box
                key={index}
                className={`file-upload-progress-bar ${
                  progressBar ? 'progress-bar' : ''
                }`}
                justifyContent={'center'}
                alignItems={'center'}
              >
                <span className="progress-bar-icon">
                  {file.error ? (
                    <ConvergeTooltip
                      title={file.error.message ?? 'Unknown error'}
                    >
                      <WarningIcon className="warning-icon" color={'error'} />
                    </ConvergeTooltip>
                  ) : (
                    <DescriptionIcon className="description-icon" />
                  )}
                </span>
                {progressBar ? (
                  <span className="progress-wrapper">
                    <LinearProgress
                      sx={{
                        width: '100%',
                        display: progressBar ? 'block' : 'none',
                      }}
                      color={file.error ? 'error' : 'primary'}
                      variant="determinate"
                      value={uploadProgress[file.id] || 0}
                      className="progress"
                    />
                  </span>
                ) : (
                  false
                )}

                <span className="progress-bar-details">
                  <span className="progress-bar-details-inner">
                    <span className="progress-bar-file-name">
                      {file.file.name}
                    </span>
                    <span className="progress-bar-file-size">
                      {bytesToSize(file.file.size)}
                    </span>
                  </span>
                  {showRemoveIcon ? (
                    <span className="progress-bar-remove-icon">
                      <IconButton
                        aria-label="remove-icon"
                        disabled={
                          uploadProgress[file.id] !== 100 && progressBar
                        }
                        className="remove-icon"
                        onClick={() => onRemove(file.id)}
                      >
                        <CloseIcon className="close-icon" />
                      </IconButton>
                    </span>
                  ) : (
                    false
                  )}
                </span>
                {progressBar && (
                  <span className="progress-bar-file-percentage">
                    <ConvergeTooltip
                      title={
                        file.error?.message ??
                        `${uploadProgress[file.id] || 0}% complete`
                      }
                    >
                      <span>
                        {file.error
                          ? 'Failed to upload'
                          : `${uploadProgress[file.id] || 0}%`}
                      </span>
                    </ConvergeTooltip>
                  </span>
                )}
              </Box>
            ))}
          </UploadProgressWrapper>
        ) : (
          false
        )}
      </UploadWrapper>
    </Grid>
  );
};
